import { useState, useEffect, useRef, useCallback, type SyntheticEvent } from 'react'
import { useNavigate, useParams, useLocation, useBlocker } from 'react-router-dom'
import { useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeftRight, Users, Receipt } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { getStates, type State } from '@/lib/api/statesService'
import {
  getPropertyById,
  getCompaniesForProperty,
  updateProperty,
  deleteProperty,
  buildPropertyFormData,
  getPropertyAuditLog,
  uploadAchForm,
  deleteAchForm,
  previewPropertyFile,
} from '../services/propertyService'
import { propertyEditSchema } from '../utils/propertyValidators'
import { DEFAULT_FORM_VALUES } from '../constants'
import type {
  PropertyConfigSubTab,
  PropertyConfigSubTabItem,
  PropertyConfigTabHandle,
  PropertyEditFormValues,
  PropertyModelConfigTabHandle,
  PropertyProductConfigTabHandle,
  PropertyTab,
} from '../types'
import { loadGoogleMapsScript } from '@/lib/utils/googleMaps'
import { geocodeAddressRaw, matchZipAndStateAngularStyle } from '@/lib/utils/geocoding'
import { mapGooglePlaceToPhysicalAddressFields } from '@/lib/utils/googlePlaceAddress'
import { hasSavedLatLng, shouldShowMapOnEditLoad } from '@/lib/utils/showMapOnEdit'
import { showSuccess, showApiError, showValidationMessage, showWarning } from '@/lib/utils/notifications'
import { sendAnalytics } from '@/lib/utils/analytics'
import { PROPERTY_EDIT_TRANSFER_HEADER_POLICIES } from '@/features/transfer'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import type { EntityEditRelatedLink } from '@/components/ui/EntityEditHeaderToolbar'
import type { EntityEditTabItem } from '@/components/ui/EntityEditTabBar'
import { normalizeW9FileList, getW9FileId, getW9FileName } from '../utils/w9Files'
import { parseDuplicateAddressError } from '../utils/extractDuplicateAddressError'
import { useBreadcrumbPageTitle } from '@/components/layout/DashboardBreadcrumbContext'
import { shouldBlockUnsavedNavigation } from '@/lib/utils/unsavedNavigationBlock'
import {
  coerceAllowDynamicAccessFee,
  buildExecutiveSummarySnapshotFromProperty,
  getTabForField,
  isPropertyTabVisible,
  getFirstVisibleTab,
  getDefaultPropertyConfigSubTab,
  isPropertyConfigSubTabVisible,
  mapPropertyToFormValues,
  buildPropertyUpdatePayload,
} from '../utils/propertyEditLogic'
import {
  PROPERTY_ADDRESS_VERIFY_HTML,
  PROPERTY_ERROR_ADDRESS_VERIFY,
} from '../constants'

/** Same idea as Company Edit: only real form controls count toward unsaved dirty. */
function isFormControlTargetForDirtyMark(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return false
  const el = target as HTMLInputElement
  const type = (el.type || '').toLowerCase()
  if (type === 'hidden' || type === 'button' || type === 'submit' || type === 'reset') return false
  return true
}

/** Angular `applyFragmentTab` — legacy product/model hashes now open Config + the matching sub-tab. */
function resolvePropertyTabFromHash(
  raw: string,
): { tab: PropertyTab; configSubTab?: PropertyConfigSubTab } | null {
  if (!raw || raw === 'statsinfo') return null
  if (raw === 'productconfig' || raw === 'productconfiginfo') {
    return { tab: 'configinfo', configSubTab: 'product' }
  }
  if (raw === 'modelconfig' || raw === 'modelconfiginfo') {
    return { tab: 'configinfo', configSubTab: 'model' }
  }
  const asTab = raw as PropertyTab
  const allowed: PropertyTab[] = [
    'propertyinfo',
    'executivesummary',
    'contactsinfo',
    'communicationsinfo',
    'bankinginfo',
    'integrationsinfo',
    'activityloginfo',
    'configinfo',
  ]
  return allowed.includes(asTab) ? { tab: asTab } : null
}

export function usePropertyEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isPropertyRole, hasPolicy, isLibertyRole } = useUserRole()

  const scopedCompanyId = user?.current_role?.company_id
  const scopedCompanyName = user?.current_role?.company_name
  const isCompanySelectionLocked =
    scopedCompanyId != null && Number(scopedCompanyId) > 0

  const canViewAudit = isLibertyRole && hasPolicy(['property_audit_view'])
  const canEditPropertyInfo = hasPolicy(['property_info_edit'])
  const canEditListed = hasPolicy(['property_listed_edit'])

  const checkTabVisible = useCallback(
    (tab: PropertyTab): boolean => isPropertyTabVisible(tab, hasPolicy),
    [hasPolicy],
  )

  const resolveFirstVisibleTab = useCallback(
    (): PropertyTab => getFirstVisibleTab(hasPolicy),
    [hasPolicy],
  )

  const rawHashTab = location.hash?.replace('#', '') ?? ''
  const hashResolved = resolvePropertyTabFromHash(rawHashTab)
  const [selectedTab, setSelectedTab] = useState<PropertyTab>(
    hashResolved?.tab || getFirstVisibleTab(hasPolicy),
  )
  const [selectedConfigSubTab, setSelectedConfigSubTab] = useState<PropertyConfigSubTab>(
    hashResolved?.configSubTab ?? getDefaultPropertyConfigSubTab(hasPolicy),
  )

  const [loading, setLoading] = useState(true)
  const [states, setStates] = useState<State[]>([])
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [addressValidated, setAddressValidated] = useState(false)
  const [isValidatingAddress, setIsValidatingAddress] = useState(false)
  const [mapMessage, setMapMessage] = useState('')
  const [showAddressValidationModal, setShowAddressValidationModal] = useState(false)
  const [addressValidationModalMessage, setAddressValidationModalMessage] = useState('')
  const [addressValidationModalTitle, setAddressValidationModalTitle] = useState('Validation Message')
  const [saveConfirm, setSaveConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [duplicateAddressConfirm, setDuplicateAddressConfirm] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showExitUnsavedConfirm, setShowExitUnsavedConfirm] = useState(false)
  const exitAfterSaveRef = useRef(false)
  const exitSourceRef = useRef<'button' | 'blocker'>('button')
  /** Set before intentional leave so `navigate()` is not intercepted by a stale dirty blocker. */
  const allowLeaveWithoutBlockRef = useRef(false)
  const [duplicateProperties, setDuplicateProperties] = useState<Array<{ name: string; id: number | string }>>([])
  const [autoOpenStaffTransfer, setAutoOpenStaffTransfer] = useState(false)
  const [transferOptionOpen, setTransferOptionOpen] = useState(false)
  const [loadedPropertyName, setLoadedPropertyName] = useState('')
  const [loadedCompanyName, setLoadedCompanyName] = useState('')
  const [propertyDetails, setPropertyDetails] = useState<Record<string, unknown> | null>(null)
  const [companyChangeConfirm, setCompanyChangeConfirm] = useState(false)
  const [auditLog, setAuditLog] = useState<Array<Record<string, unknown>>>([])
  const [notesRefreshKey, setNotesRefreshKey] = useState(0)
  const [w9Uploading, setW9Uploading] = useState(false)
  const [w9StagingName, setW9StagingName] = useState('')
  const [deleteW9Confirm, setDeleteW9Confirm] = useState<{ isOpen: boolean; fileId: string }>({
    isOpen: false,
    fileId: '',
  })
  const [enableSetAddress, setEnableSetAddress] = useState(false)
  const [showGoogleAddress, setShowGoogleAddress] = useState(false)
  const [streetAddress, setStreetAddress] = useState('')
  const [showLatLongLookup, setShowLatLongLookup] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const [isMapMessage, setIsMapMessage] = useState(false)
  const [placeDetails, setPlaceDetails] = useState<google.maps.places.PlaceResult | null>(null)

  const execSummaryDataRef = useRef<Record<string, string>>({})
  const baselineExecSummaryRef = useRef<Record<string, string>>({})
  const propertyConfigTabRef = useRef<PropertyConfigTabHandle | null>(null)
  const propertyProductConfigTabRef = useRef<PropertyProductConfigTabHandle | null>(null)
  const propertyModelConfigTabRef = useRef<PropertyModelConfigTabHandle | null>(null)
  const addressInputRef = useRef<HTMLTextAreaElement | null>(null)
  const streetAddressRef = useRef<HTMLInputElement | null>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstance = useRef<google.maps.Map | null>(null)
  const markerInstance = useRef<google.maps.Marker | null>(null)
  const originalCompanyId = useRef<string | number>('')
  const w9InputRef = useRef<HTMLInputElement>(null)

  const syncExecutiveSummaryFromProperty = useCallback((p: Record<string, unknown> | null) => {
    const snap = buildExecutiveSummarySnapshotFromProperty(p)
    baselineExecSummaryRef.current = snap
    execSummaryDataRef.current = { ...snap }
  }, [])

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    setFocus,
    trigger,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<PropertyEditFormValues>({
    resolver: zodResolver(propertyEditSchema, undefined, { mode: 'sync' }) as Resolver<PropertyEditFormValues>,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: DEFAULT_FORM_VALUES,
  })

  const watchIsMailingAddress = watch('is_mailing_address')
  const watchAddress = watch('property_address')
  const watchAddress2 = watch('property_address2')
  const watchCity = watch('property_city')
  const watchState = watch('property_state')
  const watchZip = watch('property_zipcode')
  const watchFederalId = watch('federal_id')
  const watchPropertyName = watch('property_name')
  const watchFkCompanyId = watch('fk_company_id')
  const watchLatitude = watch('latitude')
  const watchLongitude = watch('longitude')

  const prevIsMailingAddressRef = useRef<boolean | undefined>(undefined)

  useBreadcrumbPageTitle(
    propertyDetails?.property_name != null
      ? String(propertyDetails.property_name).trim() || null
      : null,
  )

  // Angular `mailingAddress()` — clear mailing fields when "Different Mailing Address?" is unchecked.
  useEffect(() => {
    if (prevIsMailingAddressRef.current === true && watchIsMailingAddress === false) {
      setValue('mailing_property_address', '', { shouldDirty: true })
      setValue('mailing_property_address2', '', { shouldDirty: true })
      setValue('mailing_property_city', '', { shouldDirty: true })
      setValue('mailing_property_state', '', { shouldDirty: true })
      setValue('mailing_property_zipcode', '', { shouldDirty: true })
    }
    prevIsMailingAddressRef.current = watchIsMailingAddress
  }, [watchIsMailingAddress, setValue])

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    shouldBlockUnsavedNavigation({
      allowLeave: allowLeaveWithoutBlockRef.current,
      hasUnsavedChanges,
      currentPathname: currentLocation.pathname,
      nextPathname: nextLocation.pathname,
    }),
  )

  useEffect(() => {
    if (blocker.state === 'blocked') {
      exitSourceRef.current = 'blocker'
      setShowExitUnsavedConfirm(true)
    }
  }, [blocker.state])

  useEffect(() => {
    if (!hasUnsavedChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasUnsavedChanges])

  // Controllers / custom selects may not bubble native input events.
  useEffect(() => {
    if (isDirty) setHasUnsavedChanges(true)
  }, [isDirty])

  const markPropertyFormDirty = useCallback(() => {
    setHasUnsavedChanges(true)
  }, [])

  const leavePropertyEdit = useCallback(() => {
    if (isPropertyRole) {
      navigate('/dashboard')
    } else {
      navigate('/property')
    }
  }, [isPropertyRole, navigate])

  const completeExitAfterSave = useCallback(() => {
    if (!exitAfterSaveRef.current) return
    exitAfterSaveRef.current = false
    setHasUnsavedChanges(false)
    setShowExitUnsavedConfirm(false)
    if (exitSourceRef.current === 'blocker') {
      exitSourceRef.current = 'button'
      if (blocker.state === 'blocked') blocker.proceed()
    } else {
      exitSourceRef.current = 'button'
      allowLeaveWithoutBlockRef.current = true
      leavePropertyEdit()
    }
  }, [blocker, leavePropertyEdit])

  useEffect(() => {
    if (addressValidated) setAddressValidated(false)
  }, [watchAddress, watchAddress2, watchCity, watchState, watchZip])

  useEffect(() => {
    getStates().then(setStates).catch(console.error)
  }, [])

  useEffect(() => {
    const raw = location.hash?.replace('#', '') ?? ''
    if (!raw || !user?.current_role) return
    const resolved = resolvePropertyTabFromHash(raw)
    if (!resolved || !checkTabVisible(resolved.tab)) return
    setSelectedTab(resolved.tab)
    if (resolved.configSubTab && isPropertyConfigSubTabVisible(resolved.configSubTab, hasPolicy)) {
      setSelectedConfigSubTab(resolved.configSubTab)
    } else if (resolved.tab === 'configinfo') {
      setSelectedConfigSubTab(getDefaultPropertyConfigSubTab(hasPolicy))
    }
  }, [location.hash, user?.current_role, checkTabVisible, hasPolicy])

  useEffect(() => {
    if (!user?.current_role) return
    if (!checkTabVisible(selectedTab)) {
      const next = resolveFirstVisibleTab()
      if (next !== selectedTab) setSelectedTab(next)
    }
  }, [selectedTab, user?.current_role, checkTabVisible, resolveFirstVisibleTab])

  useEffect(() => {
    if (!user?.current_role) return
    if (!isPropertyConfigSubTabVisible(selectedConfigSubTab, hasPolicy)) {
      setSelectedConfigSubTab(getDefaultPropertyConfigSubTab(hasPolicy))
    }
  }, [selectedConfigSubTab, user?.current_role, hasPolicy])

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getPropertyById(id)
      .then((p) => {
        const rec = p as Record<string, unknown>
        setPropertyDetails(rec)
        setLoadedPropertyName(String(p.property_name ?? ''))
        setLoadedCompanyName(String((p as { company_name?: unknown }).company_name ?? ''))
        const companyId = (rec.fk_company_id ?? rec.company_id ?? '') as string | number
        originalCompanyId.current = companyId
        reset(mapPropertyToFormValues(rec))
        syncExecutiveSummaryFromProperty(rec)
        setHasUnsavedChanges(false)
        if (hasSavedLatLng(p.latitude, p.longitude)) {
          setAddressValidated(true)
        }
        setShowMap(shouldShowMapOnEditLoad(p.latitude, p.longitude))
      })
      .catch((e) => {
        showApiError(e)
        navigate('/property')
      })
      .finally(() => setLoading(false))
  }, [id, reset, navigate, syncExecutiveSummaryFromProperty])

  useEffect(() => {
    if (isCompanySelectionLocked) {
      const label =
        (scopedCompanyName && String(scopedCompanyName).trim()) ||
        loadedCompanyName ||
        `Company ${scopedCompanyId}`
      setCompanies([{ label, value: String(scopedCompanyId) }])
      return
    }
    getCompaniesForProperty(undefined, undefined, 'all')
      .then((list) => setCompanies(list as SearchableSelectOption[]))
      .catch((e) => {
        console.error(e)
        showApiError(e)
      })
  }, [isCompanySelectionLocked, scopedCompanyId, scopedCompanyName, loadedCompanyName])

  useEffect(() => {
    if (!showGoogleAddress || !streetAddressRef.current) return
    let cancelled = false
    const init = async () => {
      try {
        await loadGoogleMapsScript()
        if (cancelled || !window.google?.maps?.places || !streetAddressRef.current) return
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
          autocompleteRef.current = null
        }
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          streetAddressRef.current,
          { componentRestrictions: { country: 'US' }, types: ['geocode'] },
        )
        autocompleteRef.current.setFields(['address_components', 'formatted_address'])
        window.google.maps.event.addListener(autocompleteRef.current, 'place_changed', () => {
          const place = autocompleteRef.current!.getPlace()
          if (!place) return
          setPlaceDetails(place)
          setEnableSetAddress(true)
          if (place.formatted_address && streetAddressRef.current) {
            streetAddressRef.current.value = place.formatted_address
            setStreetAddress(place.formatted_address)
          }
        })
      } catch { /* autocomplete is nice-to-have */ }
    }
    void init()
    return () => {
      cancelled = true
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
      autocompleteRef.current = null
    }
  }, [showGoogleAddress, selectedTab])

  useEffect(() => {
    if (!showMap || !mapRef.current) return
    let cancelled = false
    const initMap = async () => {
      const lat = Number(watchLatitude) || 30.5557898
      const lng = Number(watchLongitude) || -87.8939053
      try {
        await loadGoogleMapsScript()
        if (cancelled || !window.google?.maps || !mapRef.current) return
        if (!mapInstance.current) {
          mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center: { lat, lng },
            zoom: 14,
          })
          markerInstance.current = new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstance.current,
          })
        } else {
          mapInstance.current.setCenter({ lat, lng })
          markerInstance.current?.setPosition({ lat, lng })
        }
      } catch {
        /* map is nice-to-have */
      }
    }
    void initMap()
    return () => {
      cancelled = true
    }
  }, [showMap, watchLatitude, watchLongitude, selectedTab])

  useEffect(() => {
    if (!id || !canViewAudit) {
      setAuditLog([])
      return
    }
    getPropertyAuditLog(id).then(setAuditLog).catch(() => setAuditLog([]))
  }, [id, canViewAudit])

  const loadCompanies = async (search: string) => {
    const list = await getCompaniesForProperty(search, undefined, 'all')
    setCompanies(list as SearchableSelectOption[])
  }

  const refreshPropertyDetails = async () => {
    if (!id) return
    try {
      const p = await getPropertyById(id)
      const rec = p as Record<string, unknown>
      setPropertyDetails(rec)
      syncExecutiveSummaryFromProperty(rec)
    } catch {
      /* ignore */
    }
  }

  const refreshContactsProperty = async () => {
    if (!id) return
    const p = await getPropertyById(id)
    setPropertyDetails(p as Record<string, unknown>)
  }

  const refreshPaymentsProperty = async () => {
    if (!id) return
    const p = await getPropertyById(id)
    setPropertyDetails(p as Record<string, unknown>)
  }

  const refreshIntegrationsProperty = async () => {
    if (!id) return
    const p = await getPropertyById(id)
    setPropertyDetails(p as Record<string, unknown>)
  }

  const refreshConfigProperty = async () => {
    if (!id) return
    const p = await getPropertyById(id)
    setPropertyDetails(p as Record<string, unknown>)
    setValue(
      'allow_dynamic_access_fee',
      coerceAllowDynamicAccessFee((p as Record<string, unknown>).allow_dynamic_access_fee),
      { shouldDirty: false },
    )
  }

  const refreshProductConfigProperty = async () => {
    if (!id) return
    const p = await getPropertyById(id)
    setPropertyDetails(p as Record<string, unknown>)
  }

  const refreshModelConfigProperty = async () => {
    if (!id) return
    const p = await getPropertyById(id)
    setPropertyDetails(p as Record<string, unknown>)
  }

  const w9Files = normalizeW9FileList(propertyDetails?.w9_file)

  const handleSetPlaceAddress = async () => {
    if (placeDetails) {
      const mapped = mapGooglePlaceToPhysicalAddressFields(placeDetails)
      if (mapped) {
        setValue('property_address', mapped.address1, { shouldDirty: true, shouldValidate: true })
        setValue('property_address2', mapped.address2, { shouldDirty: true, shouldValidate: true })
        setValue('property_city', mapped.city, { shouldDirty: true, shouldValidate: true })
        setValue('property_state', mapped.state, { shouldDirty: true, shouldValidate: true })
        setValue('property_zipcode', mapped.zipcode, { shouldDirty: true, shouldValidate: true })
        if (placeDetails.formatted_address) {
          setStreetAddress(placeDetails.formatted_address)
          if (streetAddressRef.current) {
            streetAddressRef.current.value = placeDetails.formatted_address
          }
        }
      }
    }
    setEnableSetAddress(false)
    setShowLatLongLookup(true)
    await trigger([
      'property_address',
      'property_city',
      'property_state',
      'property_zipcode',
    ])
  }

  const handleAddressNotFound = async () => {
    setShowLatLongLookup(true)
    await trigger([
      'property_address',
      'property_city',
      'property_state',
      'property_zipcode',
    ])
  }

  const handleW9Upload = async (files: FileList | null) => {
    if (!files?.length || !id) return
    for (const file of Array.from(files)) {
      if (file.size / 1024 / 1024 > 2) {
        showValidationMessage('File Size Exceeds Allowable Limit of 2 MB.')
        return
      }
    }
    setW9StagingName(files[0]?.name ?? '')
    setW9Uploading(true)
    try {
      const fed = (watchFederalId || '').trim()
      await uploadAchForm(id, 'w9_file', files, fed || undefined)
      showSuccess('File Uploaded Successfully!')
      await refreshPropertyDetails()
    } catch (err) {
      showApiError(err)
    } finally {
      setW9Uploading(false)
      setW9StagingName('')
      if (w9InputRef.current) w9InputRef.current.value = ''
    }
  }

  const handleW9Download = async (fileId: string) => {
    if (!id || !fileId) return
    const fileItem = w9Files.find((f) => getW9FileId(f) === fileId)
    const idx = fileItem ? w9Files.indexOf(fileItem) : 0
    const name = fileItem ? getW9FileName(fileItem, idx) : 'W-9'
    try {
      await previewPropertyFile(id, 'W-9', fileId, name)
    } catch (err) {
      showApiError(err)
    }
  }

  const executeW9Delete = async () => {
    const fid = deleteW9Confirm.fileId
    setDeleteW9Confirm({ isOpen: false, fileId: '' })
    if (!fid) return
    try {
      await deleteAchForm(fid)
      showSuccess('Document file removed.')
      await refreshPropertyDetails()
    } catch (err) {
      showApiError(err)
    }
  }

  const doUpdate = async (saveAddress: boolean) => {
    if (!id) return
    sendAnalytics('click', 'edit property', id)
    const data = watch()

    const payload = buildPropertyUpdatePayload({
      data,
      propertyDetails,
      execSummaryData: execSummaryDataRef.current,
      isLibertyRole,
      skipAln: String(propertyDetails?.is_skip_aln ?? '') === '1',
    })

    const formData = buildPropertyFormData(payload, { saveAddress })
    // Angular editProperty: selected_products on main multipart POST (not /api/property/product).
    const selectedProductsJson =
      propertyProductConfigTabRef.current?.getSelectedProductsJson?.() ?? null
    if (selectedProductsJson) {
      formData.append('selected_products', selectedProductsJson)
    }
    // Angular editProperty: selected_models on the same POST, only when the model policy allows it.
    if (hasPolicy(['allow_model_config'])) {
      const selectedModelsJson =
        propertyModelConfigTabRef.current?.getSelectedModelsJson?.() ?? null
      if (selectedModelsJson) {
        formData.append('selected_models', selectedModelsJson)
      }
    }
    await updateProperty(id, formData)
    showSuccess('Property Updated  Successfully!')
    setNotesRefreshKey((k) => k + 1)
    if (canViewAudit) {
      getPropertyAuditLog(id).then(setAuditLog).catch(() => {})
    }
    setSaveConfirm(false)
    setDuplicateAddressConfirm(false)
    setHasUnsavedChanges(false)
    if (id) {
      try {
        const p = await getPropertyById(id)
        const rec = p as Record<string, unknown>
        // Angular editProperty → getPropertyDetails(): re-feed the fresh snapshot so the
        // Product Config tab reloads current_products and just-saved rows inherit their
        // server property_product_map_id (else a later delete errors "missing product map id").
        setPropertyDetails(rec)
        const rCompanyId = (rec.fk_company_id ?? rec.company_id ?? '') as string | number
        originalCompanyId.current = rCompanyId
        reset(mapPropertyToFormValues(rec))
        syncExecutiveSummaryFromProperty(rec)
        setHasUnsavedChanges(false)
      } catch {
        // Optional property fields may be absent during executive-summary sync.
      }
    }
    completeExitAfterSave()
  }

  const onFormError = (fieldErrors: FieldErrors<PropertyEditFormValues>) => {
    const firstKey = Object.keys(fieldErrors)[0] as keyof PropertyEditFormValues | undefined
    if (!firstKey) return

    const targetTab = getTabForField(String(firstKey))
    if (selectedTab !== targetTab) {
      setSelectedTab(targetTab)
    }

    showWarning('Please fix the highlighted errors before saving.')

    requestAnimationFrame(() => {
      void setFocus(firstKey)
      requestAnimationFrame(() => {
        const el =
          document.querySelector<HTMLElement>(`#property-edit-form [name="${String(firstKey)}"]`) ??
          document.querySelector<HTMLElement>(`[name="${String(firstKey)}"]`)
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    })
  }

  /**
   * Angular submitForm: product/model rows are validated BEFORE the "Save Changes?" confirm,
   * so invalid rows surface their inline errors instead of opening the dialog.
   */
  const validateConfigRowsBeforeSave = (): boolean => {
    const productValid =
      propertyProductConfigTabRef.current?.validateProductRowsForSubmit?.() ?? true
    const modelValid = hasPolicy(['allow_model_config'])
      ? propertyModelConfigTabRef.current?.validateModelRowsForSubmit?.() ?? true
      : true

    if (productValid && modelValid) return true

    setSelectedTab('configinfo')
    const targetSubTab: PropertyConfigSubTab = productValid ? 'model' : 'product'
    if (isPropertyConfigSubTabVisible(targetSubTab, hasPolicy)) {
      setSelectedConfigSubTab(targetSubTab)
    }
    showWarning('Please fix the highlighted errors before saving.')
    return false
  }

  const onSubmit = async () => {
    if (!validateConfigRowsBeforeSave()) return
    setSaveConfirm(true)
  }

  const handleConfirmSave = async () => {
    if (!id) return
    const values = getValues()

    setSaveConfirm(false)
    setAddressValidated(false)

    const prevCompanyId = String(originalCompanyId.current)
    const companyAtSave = String(values.fk_company_id)

    setIsSubmitting(true)
    const leaveAfterThisSave = exitAfterSaveRef.current
    try {
      const productOk =
        (await propertyProductConfigTabRef.current?.saveProductsIfDirtyFromParent({
          silentSuccess: true,
        })) ?? true
      if (!productOk) {
        exitAfterSaveRef.current = false
        if (blocker.state === 'blocked') blocker.reset()
        exitSourceRef.current = 'button'
        return
      }
      const modelOk =
        (await propertyModelConfigTabRef.current?.saveModelsIfDirtyFromParent({
          silentSuccess: true,
        })) ?? true
      if (!modelOk) {
        exitAfterSaveRef.current = false
        if (blocker.state === 'blocked') blocker.reset()
        exitSourceRef.current = 'button'
        return
      }
      await doUpdate(false)
      if (!leaveAfterThisSave && companyAtSave !== prevCompanyId) {
        setCompanyChangeConfirm(true)
      }
    } catch (err: unknown) {
      exitAfterSaveRef.current = false
      if (blocker.state === 'blocked') blocker.reset()
      exitSourceRef.current = 'button'
      const dupes = parseDuplicateAddressError(err)
      if (dupes) {
        setDuplicateProperties(dupes)
        setDuplicateAddressConfirm(true)
      } else {
        showApiError(err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleValidateAddress = async () => {
    const ok = await trigger([
      'property_address',
      'property_city',
      'property_state',
      'property_zipcode',
    ])
    if (!ok) return
    const data = watch()
    const fullAddress = [
      data.property_address,
      data.property_address2,
      data.property_city,
      data.property_state,
      data.property_zipcode,
    ]
      .filter(Boolean)
      .join(' ')
    setStreetAddress(fullAddress)
    setShowGoogleAddress(true)
    setEnableSetAddress(true)
    setPlaceDetails(null)
    // Re-open Google lookup: unlock fields so incomplete results can be corrected.
    setShowLatLongLookup(false)
    if (streetAddressRef.current) streetAddressRef.current.value = fullAddress
  }

  const handleLatLongLookup = async () => {
    const ok = await trigger([
      'property_address',
      'property_city',
      'property_state',
      'property_zipcode',
    ])
    if (!ok) return
    await runGeocodeLookup('LatLong')
  }

  const refreshMap = () => {
    void runGeocodeLookup('refresh')
  }

  const failPropertyGeocodeLookup = (opts: { notify: boolean }) => {
    setShowMap(true)
    setIsMapMessage(true)
    setMapMessage(PROPERTY_ERROR_ADDRESS_VERIFY)
    if (opts.notify) {
      setAddressValidationModalTitle('Validation Message')
      setAddressValidationModalMessage(PROPERTY_ADDRESS_VERIFY_HTML)
      setShowAddressValidationModal(true)
    }
  }

  const runGeocodeLookup = async (mode: 'LatLong' | 'refresh') => {
    const data = watch()
    const fullAddress = [
      data.property_address,
      data.property_address2,
      data.property_city,
      data.property_state,
      data.property_zipcode,
    ]
      .filter(Boolean)
      .join(' ')
    setShowMap(true)
    setIsValidatingAddress(true)
    setMapMessage('')
    const notify = mode === 'LatLong'

    try {
      const { result, status } = await geocodeAddressRaw(fullAddress)

      if (
        status !== google.maps.GeocoderStatus.OK ||
        !result ||
        !result.geometry ||
        result.partial_match
      ) {
        failPropertyGeocodeLookup({ notify })
        return
      }

      const { zipValid, stateValid } = matchZipAndStateAngularStyle(
        result,
        data.property_zipcode,
        data.property_state,
      )

      if (!zipValid || !stateValid) {
        failPropertyGeocodeLookup({ notify })
        return
      }

      const lat = result.geometry.location.lat()
      const lng = result.geometry.location.lng()
      setValue('latitude', String(lat), { shouldDirty: true })
      setValue('longitude', String(lng), { shouldDirty: true })
      setAddressValidated(true)
      setHasUnsavedChanges(true)
      setMapMessage('')
      setIsMapMessage(false)
      setShowMap(true)

      if (mode === 'LatLong') {
        setAddressValidationModalTitle('Verification Successful!')
        setAddressValidationModalMessage(
          `Address verified with Latitude: ${lat} Longitude: ${lng}`,
        )
        setShowAddressValidationModal(true)
        await handleConfirmSave()
      }
    } catch (err) {
      if (mode === 'LatLong') showApiError(err)
      failPropertyGeocodeLookup({ notify })
    } finally {
      setIsValidatingAddress(false)
    }
  }

  const handleDuplicateSave = async () => {
    if (!id) return
    setDuplicateAddressConfirm(false)
    setIsSubmitting(true)
    try {
      await doUpdate(true)
    } catch (err) {
      exitAfterSaveRef.current = false
      if (blocker.state === 'blocked') blocker.reset()
      exitSourceRef.current = 'button'
      showApiError(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return
    setHasUnsavedChanges(false)
    allowLeaveWithoutBlockRef.current = true
    setIsSubmitting(true)
    try {
      await deleteProperty(id)
      showSuccess('Property Deleted Successfully!')
      navigate('/property')
    } catch (err) {
      allowLeaveWithoutBlockRef.current = false
      showApiError(err)
    } finally {
      setIsSubmitting(false)
      setDeleteConfirm(false)
    }
  }

  const handleCompanyChangeConfirmOk = () => {
    setCompanyChangeConfirm(false)
    setSelectedTab('contactsinfo')
    setAutoOpenStaffTransfer(true)
  }

  const onExecutiveSummaryDataChange = (data: Record<string, string>) => {
    execSummaryDataRef.current = data
    setHasUnsavedChanges(true)
  }

  const handleStayOnPage = useCallback(() => {
    setShowExitUnsavedConfirm(false)
    exitAfterSaveRef.current = false
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
    exitSourceRef.current = 'button'
  }, [blocker])

  const handleExitWithoutSaving = useCallback(() => {
    setShowExitUnsavedConfirm(false)
    setHasUnsavedChanges(false)
    exitAfterSaveRef.current = false
    if (exitSourceRef.current === 'blocker') {
      exitSourceRef.current = 'button'
      if (blocker.state === 'blocked') blocker.proceed()
    } else {
      exitSourceRef.current = 'button'
      allowLeaveWithoutBlockRef.current = true
      leavePropertyEdit()
    }
  }, [blocker, leavePropertyEdit])

  const handleSaveFromExitDialog = () => {
    setShowExitUnsavedConfirm(false)
    exitAfterSaveRef.current = true
    void handleConfirmSave()
  }

  const propertyHeaderActionsDisabled =
    isSubmitting ||
    isValidatingAddress ||
    saveConfirm ||
    showExitUnsavedConfirm ||
    deleteConfirm ||
    duplicateAddressConfirm ||
    companyChangeConfirm ||
    transferOptionOpen ||
    deleteW9Confirm.isOpen

  const requestLeavePropertyEdit = useCallback(() => {
    if (
      isSubmitting ||
      isValidatingAddress ||
      saveConfirm ||
      showExitUnsavedConfirm ||
      deleteConfirm ||
      duplicateAddressConfirm ||
      companyChangeConfirm ||
      transferOptionOpen ||
      deleteW9Confirm.isOpen
    ) {
      return
    }
    if (!hasUnsavedChanges) {
      leavePropertyEdit()
      return
    }
    exitSourceRef.current = 'button'
    setShowExitUnsavedConfirm(true)
  }, [
    isSubmitting,
    isValidatingAddress,
    saveConfirm,
    showExitUnsavedConfirm,
    deleteConfirm,
    duplicateAddressConfirm,
    companyChangeConfirm,
    transferOptionOpen,
    deleteW9Confirm.isOpen,
    hasUnsavedChanges,
    leavePropertyEdit,
  ])

  const handleCancel = requestLeavePropertyEdit

  const onDirtyFormCapture = useCallback((e: SyntheticEvent) => {
    if (
      e.target instanceof Element &&
      e.target.closest('[data-independent-save-scope="true"]')
    ) {
      return
    }
    if (isFormControlTargetForDirtyMark(e.target)) {
      setHasUnsavedChanges(true)
    }
  }, [])

  const displayName = watchPropertyName || loadedPropertyName
  const displayCompany =
    loadedCompanyName ||
    (watchFkCompanyId && companies.find((c) => c.value === String(watchFkCompanyId))?.label)

  const propertyEditRelatedLinks: EntityEditRelatedLink[] = []
  if (hasPolicy([...PROPERTY_EDIT_TRANSFER_HEADER_POLICIES]) && id) {
    propertyEditRelatedLinks.push({
      key: 'transfer',
      label: 'Transfer',
      icon: ArrowLeftRight,
      testId: 'property-edit-transfer-button',
      onClick: () => {
        if (propertyHeaderActionsDisabled) return
        setTransferOptionOpen(true)
      },
    })
  }
  propertyEditRelatedLinks.push(
    {
      key: 'residents',
      label: 'Residents',
      icon: Users,
      testId: 'property-edit-residents-button',
      onClick: () => {
        if (propertyHeaderActionsDisabled || !id) return
        sessionStorage.setItem(
          'residentlist_filter',
          JSON.stringify({
            propertyId: String(id),
            property_name: displayName || '',
            past_customer: '1',
          }),
        )
        navigate('/resident')
      },
    },
    {
      key: 'reimbursements',
      label: 'Reimbursements',
      icon: Receipt,
      testId: 'property-edit-reimburse-button',
      onClick: () => {
        if (propertyHeaderActionsDisabled || !id) return
        const rawOrgId = propertyDetails?.org_id
        const orgIdHandoff =
          rawOrgId != null && (typeof rawOrgId === 'string' || typeof rawOrgId === 'number')
            ? rawOrgId
            : null
        sessionStorage.setItem(
          'defaultlist_filter',
          JSON.stringify({
            __source: 'reimbursements_react_v2',
            propertyId: String(id),
            property_name: displayName || '',
            past_customer: '1',
            org_id: orgIdHandoff || '',
          }),
        )
        navigate('/defaults')
      },
    },
  )

  const propertyEditTabItems: EntityEditTabItem[] = (
    [
      { id: 'propertyinfo', label: 'Property Info' },
      { id: 'executivesummary', label: 'Executive Summary' },
      { id: 'contactsinfo', label: 'Contacts' },
      { id: 'communicationsinfo', label: 'Communications' },
      { id: 'bankinginfo', label: 'Payments' },
      { id: 'integrationsinfo', label: 'Integrations' },
      { id: 'activityloginfo', label: 'Activity' },
      { id: 'configinfo', label: 'Config' },
    ] as { id: PropertyTab; label: string }[]
  )
    .filter((t) => checkTabVisible(t.id))
    .map((t) => ({
      id: t.id,
      label: t.label,
      testId: `property-edit-tab-${t.id}`,
    }))

  const propertyConfigSubTabItems: PropertyConfigSubTabItem[] = (
    [
      { id: 'yardi', label: 'Yardi' },
      { id: 'product', label: 'Product' },
      { id: 'model', label: 'Model' },
    ] as PropertyConfigSubTabItem[]
  ).filter((t) => isPropertyConfigSubTabVisible(t.id, hasPolicy))

  const auditRows = Array.isArray(auditLog) ? auditLog : []

  return {
    loading,
    id,
    navigate,
    location,
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    setFocus,
    trigger,
    reset,
    errors,
    isDirty,
    dirtyFields,
    selectedTab,
    setSelectedTab,
    selectedConfigSubTab,
    setSelectedConfigSubTab,
    propertyConfigSubTabItems,
    propertyEditTabItems,
    propertyDetails,
    setPropertyDetails,
    loadedPropertyName,
    loadedCompanyName,
    displayName,
    displayCompany,
    states,
    companies,
    isCompanySelectionLocked,
    scopedCompanyId,
    scopedCompanyName,
    hasPolicy,
    isPropertyRole,
    isLibertyRole,
    canViewAudit,
    canEditPropertyInfo,
    canEditListed,
    isPropertyTabVisible: checkTabVisible,
    addressInputRef,
    streetAddressRef,
    enableSetAddress,
    showGoogleAddress,
    streetAddress,
    setStreetAddress,
    showLatLongLookup,
    showMap,
    isMapMessage,
    mapRef,
    refreshMap,
    handleSetPlaceAddress,
    handleAddressNotFound,
    isValidatingAddress,
    mapMessage,
    handleValidateAddress,
    handleLatLongLookup,
    showAddressValidationModal,
    setShowAddressValidationModal,
    addressValidationModalMessage,
    addressValidationModalTitle,
    addressValidated,
    w9Files,
    w9Uploading,
    w9StagingName,
    w9InputRef,
    handleW9Upload,
    handleW9Download,
    deleteW9Confirm,
    setDeleteW9Confirm,
    executeW9Delete,
    isSubmitting,
    saveConfirm,
    setSaveConfirm,
    deleteConfirm,
    setDeleteConfirm,
    duplicateAddressConfirm,
    setDuplicateAddressConfirm,
    duplicateProperties,
    companyChangeConfirm,
    setCompanyChangeConfirm,
    onSubmit,
    onFormError,
    handleConfirmSave,
    handleDelete,
    handleDuplicateSave,
    handleCompanyChangeConfirmOk,
    autoOpenStaffTransfer,
    setAutoOpenStaffTransfer,
    transferOptionOpen,
    setTransferOptionOpen,
    execSummaryDataRef,
    propertyConfigTabRef,
    propertyProductConfigTabRef,
    propertyModelConfigTabRef,
    originalCompanyId,
    auditLog,
    auditRows,
    notesRefreshKey,
    refreshPropertyDetails,
    refreshContactsProperty,
    refreshPaymentsProperty,
    refreshIntegrationsProperty,
    refreshConfigProperty,
    refreshProductConfigProperty,
    refreshModelConfigProperty,
    loadCompanies,
    propertyHeaderActionsDisabled,
    handleCancel,
    showExitUnsavedConfirm,
    hasUnsavedChanges,
    handleStayOnPage,
    handleExitWithoutSaving,
    handleSaveFromExitDialog,
    onDirtyFormCapture,
    markPropertyFormDirty,
    propertyEditRelatedLinks,
    watchIsMailingAddress,
    onExecutiveSummaryDataChange,
  }
}
